import React, { useState, useEffect, useRef } from 'react';
import './Pages.css';
import Navbar from '../components/Navbar';
import { supabase } from '../config/supabase';

function DMA() {
  const [elements, setElements] = useState({
    env: [
      { id: 'climate', name: 'Climate Change & GHG Emissions', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'energy', name: 'Energy Management', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'water', name: 'Water & Wastewater Management', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'biodiversity', name: 'Biodiversity Impact', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'waste', name: 'Waste & Hazardous Materials', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'air', name: 'Air Quality', selected: false, comment: '', financialScore: 0, impactScore: 0 }
    ],
    soc: [
      { id: 'labor', name: 'Labor Practices & Rights', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'health', name: 'Employee Health & Safety', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'diversity', name: 'Diversity & Inclusion', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'community', name: 'Community Relations', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'human', name: 'Human Rights', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'privacy', name: 'Data Privacy & Security', selected: false, comment: '', financialScore: 0, impactScore: 0 }
    ],
    gov: [
      { id: 'ethics', name: 'Business Ethics', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'compliance', name: 'Regulatory Compliance', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'corruption', name: 'Anti-corruption', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'transparency', name: 'Transparency', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'risk', name: 'Risk Management', selected: false, comment: '', financialScore: 0, impactScore: 0 },
      { id: 'board', name: 'Board Structure & Oversight', selected: false, comment: '', financialScore: 0, impactScore: 0 }
    ]
  });
  
  const [selectedCategory, setSelectedCategory] = useState('env');
  const [customElement, setCustomElement] = useState('');
  const [showMatrix, setShowMatrix] = useState(false);
  const canvasRef = useRef(null);

  const categories = [
    { id: 'env', label: 'Environmental', color: '#4CAF50' },
    { id: 'soc', label: 'Social', color: '#9C27B0' },
    { id: 'gov', label: 'Governance', color: '#FF9800' }
  ];

  // Toggle element selection
  const toggleElement = (categoryId, elementId) => {
    setElements(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].map(element => 
        element.id === elementId 
          ? { ...element, selected: !element.selected }
          : element
      )
    }));
  };

  // Update element scores
  const updateElementScore = (categoryId, elementId, type, value) => {
    setElements(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].map(element => 
        element.id === elementId 
          ? { ...element, [type]: parseInt(value) }
          : element
      )
    }));
  };

  // Update element comment
  const updateElementComment = (categoryId, elementId, comment) => {
    setElements(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].map(element => 
        element.id === elementId 
          ? { ...element, comment }
          : element
      )
    }));
  };

  // Add custom element
  const addCustomElement = () => {
    if (customElement.trim()) {
      const newElement = {
        id: `custom-${Date.now()}`,
        name: customElement.trim(),
        selected: true,
        comment: '',
        financialScore: 0,
        impactScore: 0,
        isCustom: true
      };

      setElements(prev => ({
        ...prev,
        [selectedCategory]: [...prev[selectedCategory], newElement]
      }));
      setCustomElement('');
    }
  };

  // Remove element
  const removeElement = (categoryId, elementId) => {
    setElements(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter(element => element.id !== elementId)
    }));
  };

  // Matrix visualization effect
  useEffect(() => {
    if (!showMatrix || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = 600;
    const height = 600;
    const padding = 60;
    
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Calculate the actual plotting area
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Draw light blue background for entire plot area
    ctx.fillStyle = 'rgba(240, 249, 255, 0.5)';
    ctx.fillRect(padding, padding, plotWidth, plotHeight);

    // Draw grid zones (4x4 grid)
    const zoneWidth = plotWidth / 4;
    const zoneHeight = plotHeight / 4;

    // Draw horizontal and vertical zone lines (darker than grid lines)
    ctx.strokeStyle = 'rgba(147, 197, 253, 0.5)';
    ctx.lineWidth = 1;
    
    // Vertical zone lines
    for (let i = 0; i <= 4; i++) {
      const x = padding + (plotWidth * (i / 4));
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }
    
    // Horizontal zone lines
    for (let i = 0; i <= 4; i++) {
      const y = padding + (plotHeight * (i / 4));
      ctx.beginPath();
      ctx.moveTo(padding, y);
      ctx.lineTo(width - padding, y);
      ctx.stroke();
    }

    // Draw grid lines (lighter than zone lines)
    ctx.strokeStyle = 'rgba(147, 197, 253, 0.3)';
    ctx.lineWidth = 0.5;
    
    // Draw grid lines and labels
    for (let i = 0; i <= 5; i++) {
      const x = padding + (plotWidth * (i / 5));
      const y = height - padding - (plotHeight * (i / 5));
      
      // Grid lines
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(padding, height - padding - (plotHeight * (i / 5)));
      ctx.lineTo(width - padding, height - padding - (plotHeight * (i / 5)));
      ctx.stroke();
    }

    // Draw zone labels
    ctx.font = '12px Arial';
    ctx.fillStyle = '#1f2937';
    ctx.textAlign = 'center';
    
    // X-axis zone labels
    const xLabels = ['Informative', 'Importante', 'Très importante', 'Critique'];
    xLabels.forEach((label, i) => {
      const x = padding + (plotWidth * ((i + 0.5) / 4));
      ctx.fillText(label, x, height - padding + 25);
    });
    
    // Y-axis zone labels
    ctx.save();
    ctx.rotate(-Math.PI / 2);
    const yLabels = ['Informative', 'Importante', 'Très importante', 'Critique'];
    yLabels.forEach((label, i) => {
      const y = padding + (plotHeight * ((i + 0.5) / 4));
      ctx.fillText(label, -height + y, padding - 30);
    });
    ctx.restore();

    // Draw axes labels
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 14px Arial';
    
    // X-axis label
    ctx.textAlign = 'center';
    ctx.fillText('Matérialité financière', width / 2, height - 5);
    
    // Y-axis label
    ctx.save();
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Matérialité d\'impact', -height / 2, 15);
    ctx.restore();

    // Plot elements
    Object.entries(elements).forEach(([category, items]) => {
      items.forEach(element => {
        if (element.selected) {
          const x = padding + (plotWidth * element.financialScore / 5);
          const y = height - padding - (plotHeight * element.impactScore / 5);

          // Draw point
          ctx.beginPath();
          ctx.arc(x, y, 6, 0, 2 * Math.PI);
          ctx.fillStyle = categories.find(cat => cat.id === category)?.color || '#000';
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw label with background
          const label = element.name;
          ctx.font = '12px Arial';
          const metrics = ctx.measureText(label);
          const labelPadding = 4;
          const labelHeight = 20;
          
          // Determine label position to avoid going off canvas
          let labelX = x + 12;
          let labelY = y - labelHeight/2;
          
          // Adjust if too close to right edge
          if (labelX + metrics.width + labelPadding * 2 > width - padding) {
            labelX = x - metrics.width - labelPadding * 2 - 12;
          }
          
          // Draw label background
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.fillRect(
            labelX,
            labelY,
            metrics.width + labelPadding * 2,
            labelHeight
          );
          
          // Draw label text
          ctx.fillStyle = '#1f2937';
          ctx.textAlign = 'left';
          ctx.fillText(label, labelX + labelPadding, labelY + 14);
        }
      });
    });

  }, [showMatrix, elements, categories]);

  return (
    <>
      <style>
        {`
          .dma-container {
            padding: 2rem;
            margin-top: 70px;
            min-height: calc(100vh - 70px);
            position: relative;
            z-index: 1;
          }

          .matrix-visualization {
            display: flex;
            align-items: flex-start;
            gap: 2rem;
            margin-top: 2rem;
          }

          .matrix-container {
            flex: 1;
            max-width: 600px;
            background: white;
            padding: 1rem;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .matrix-legend {
            width: 200px;
            padding: 1rem;
            background: white;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .matrix-legend h3 {
            margin: 0 0 1rem 0;
            font-size: 1rem;
            color: #1f2937;
          }

          .legend-item {
            display: flex;
            align-items: center;
            margin-bottom: 0.75rem;
          }

          .legend-color {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            margin-right: 0.75rem;
            border: 2px solid white;
            box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
          }

          .legend-label {
            font-size: 0.875rem;
            color: #1f2937;
          }

          .dma-section {
            background: white;
            border-radius: 8px;
            padding: 1.5rem;
            margin-bottom: 2rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          @media (max-width: 768px) {
            .matrix-visualization {
              flex-direction: column;
            }

            .matrix-legend {
              width: 100%;
              margin-top: 1rem;
            }

            .matrix-container {
              max-width: 100%;
            }
          }
        `}
      </style>
      <div className="page-wrapper">
        <Navbar />
        <main className="dma-container">
          <h1>Double Materiality Analysis</h1>
          
          <div className="dma-content">
            {/* Topic Selection Section */}
            <section className="dma-section">
              <h2>Topic Selection</h2>
              <div className="category-tabs">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`category-tab ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category.id)}
                    style={{ borderColor: category.color }}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              <div className="elements-list">
                {elements[selectedCategory].map(element => (
                  <div key={element.id} className="element-item">
                    <label className="element-label">
                      <input
                        type="checkbox"
                        checked={element.selected}
                        onChange={() => toggleElement(selectedCategory, element.id)}
                      />
                      <span className="element-name">{element.name}</span>
                    </label>
                    {element.isCustom && (
                      <button
                        className="remove-element-btn"
                        onClick={() => removeElement(selectedCategory, element.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="custom-element-input">
                <input
                  type="text"
                  value={customElement}
                  onChange={(e) => setCustomElement(e.target.value)}
                  placeholder="Add a custom topic..."
                  className="custom-input"
                />
                <button
                  onClick={addCustomElement}
                  disabled={!customElement.trim()}
                  className="add-custom-btn"
                >
                  Add Topic
                </button>
              </div>
            </section>

            {/* Assessment Section */}
            <section className="dma-section">
              <h2>Impact Assessment</h2>
              <div className="assessment-list">
                {Object.entries(elements).map(([category, items]) => (
                  items.filter(element => element.selected).map(element => (
                    <div key={element.id} className="assessment-item">
                      <div className="topic-name" style={{ color: categories.find(cat => cat.id === category)?.color }}>
                        {element.name}
                      </div>
                      <div className="assessment-inputs">
                        <div className="input-group">
                          <label>Financial Materiality (0-5):</label>
                          <input
                            type="range"
                            min="0"
                            max="5"
                            step="1"
                            value={element.financialScore}
                            onChange={(e) => updateElementScore(category, element.id, 'financialScore', e.target.value)}
                          />
                          <span>{element.financialScore}</span>
                        </div>
                        <div className="input-group">
                          <label>Impact Materiality (0-5):</label>
                          <input
                            type="range"
                            min="0"
                            max="5"
                            step="1"
                            value={element.impactScore}
                            onChange={(e) => updateElementScore(category, element.id, 'impactScore', e.target.value)}
                          />
                          <span>{element.impactScore}</span>
                        </div>
                        <div className="comment-input-group">
                          <label>Comments:</label>
                          <textarea
                            value={element.comment}
                            onChange={(e) => updateElementComment(category, element.id, e.target.value)}
                            placeholder="Add your comments here..."
                            rows="3"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ))}
              </div>
            </section>

            {/* Matrix Visualization Section */}
            <section className="dma-section">
              <h2>Materiality Matrix</h2>
              <button
                onClick={() => setShowMatrix(true)}
                className="generate-matrix-btn"
              >
                Generate Matrix
              </button>

              {showMatrix && (
                <div className="matrix-visualization">
                  <div className="matrix-container">
                    <canvas
                      ref={canvasRef}
                      width={600}
                      height={600}
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="matrix-legend">
                    <h3>Legend</h3>
                    {categories.map((category) => (
                      <div key={category.id} className="legend-item">
                        <span 
                          className="legend-color" 
                          style={{ backgroundColor: category.color }}
                        ></span>
                        <span className="legend-label">{category.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>
    </>
  );
}

export default DMA; 